'use client';

import { NavigableButton } from '@carvana/ds-button';
import cx from 'classnames';
import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIsMobile } from '~/context/common/DeviceContext';
import useConfiguratorNavItems, {
  NavItem
} from '~/hooks/new-cars/useConfiguratorNavItems';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import { NavContainer, NavItemWrapper } from './ConfiguratorNavigation.styles';

function NavButton({ href, isCompleted, label, onClick, status }: NavItem) {
  const isActive = status === 'active';
  const isDisabled = status === 'disabled';
  const isNonNavigable = isActive && !href && !onClick;

  return (
    <NavigableButton
      data-adobe-id={null}
      className={cx(
        'font-semibold transition-all duration-300 hover:text-text-strong focus:bg-background-primary-subtle focus:text-text-primary md:w-full md:flex md:justify-start',
        isActive
          ? 'bg-background-primary-subtle text-text-primary hover:bg-background-primary-subtle'
          : 'text-text-strong',
        { '!bg-background-default !text-text-disabled': isDisabled },
        { 'pointer-events-none': isNonNavigable }
      )}
      disabled={isDisabled}
      data-analytics-attributes={JSON.stringify({
        text: label
      })}
      data-analytics-id="Configurator Navigation"
      data-testid="navigation-link"
      href={href}
      onClick={onClick}
      size="sm"
      variant="primaryInverted"
    >
      {label}
      {isCompleted && <Check size={16} />}
    </NavigableButton>
  );
}

function ConfiguratorNavigation() {
  const isMobile = useIsMobile();
  const navItems = useConfiguratorNavItems();
  const navRef = useRef<HTMLElement>(null);

  const activeLabel = navItems.find(item => item.status === 'active')?.label;

  useEffect(() => {
    const activeItem = navRef.current?.querySelector<HTMLElement>(
      '[data-active="true"]'
    );
    if (!activeItem) return;

    if (isMobile) {
      const prevItem = activeItem.previousElementSibling as HTMLElement | null;
      const scrollTarget = prevItem ?? activeItem;
      scrollTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    } else {
      activeItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [activeLabel, isMobile]);

  return (
    <NavContainer aria-label="Configuration steps" ref={navRef}>
      {navItems
        .filter(navItem => navItem.isVisible)
        .map(navItem => (
          <NavItemWrapper
            data-active={navItem.status === 'active'}
            isVisible={navItem.isVisible}
            key={navItem.label}
          >
            <NavButton {...navItem} />
          </NavItemWrapper>
        ))}
    </NavContainer>
  );
}

export default withErrorBoundary(
  ConfiguratorNavigation,
  undefined,
  undefined,
  'navigation'
);
